import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Avatar } from '../ui/Avatar';
import { LeaveTypeBadge } from '../ui/LeaveTypeBadge';
import { format, parseISO, isAfter, isSameDay } from 'date-fns';
import apiService from '../../services/api';

export function UpcomingHolidays() {
  const { getEmployee, employees } = useApp();
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    apiService.getLeaveRequests({ scope: 'all', limit: 200 })
      .then(res => {
        const all = res?.data?.requests ?? res?.data ?? res ?? [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const filtered = Array.isArray(all)
          ? all
              .filter(r => {
                if (r.status !== 'approved') return false;
                try {
                  const from = parseISO(r.from);
                  return isAfter(from, today) || isSameDay(from, today);
                } catch { return false; }
              })
              .sort((a, b) => parseISO(a.from) - parseISO(b.from))
              .slice(0, 5)
          : [];

        setUpcoming(filtered);
      })
      .catch(err => {
        console.error('UpcomingHolidays fetch error:', err);
        setUpcoming([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Resolve employee — handles empId or userId field
  function resolveEmp(r) {
    const id = r.empId ?? r.userId;
    if (!id) return null;
    // Try context first
    const fromCtx = getEmployee?.(id);
    if (fromCtx) return fromCtx;
    // Fall back to employees array
    return employees?.find(e => e.id === id) ?? null;
  }

  // Format duration — handles half days
  function fmtDuration(r) {
    const days = parseFloat(r.days);
    if (!isNaN(days)) {
      if (days === 0.5) {
        const period = r.period ?? r.halfDayPeriod;
        return period === 'PM' ? '½ day (PM)' : '½ day (AM)';
      }
      return `${days}d`;
    }
    return null;
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold th-text">Upcoming Approved Leave</h3>
        <span className="text-xs th-muted">
          {loading ? '…' : `${upcoming.length} upcoming`}
        </span>
      </div>

      {loading ? (
        <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          Loading…
        </div>
      ) : upcoming.length === 0 ? (
        <p className="text-sm th-muted text-center py-4">No upcoming leave scheduled</p>
      ) : (
        <div className="flex flex-col divide-y divide-[color:var(--border-soft)]">
          {upcoming.map(r => {
            const emp      = resolveEmp(r);
            const duration = fmtDuration(r);
            // Use employee name from the request itself as fallback
            const name = emp?.name
              ?? (r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : null)
              ?? 'Unknown';

            return (
              <div key={r.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                {emp
                  ? <Avatar employee={emp} size="sm" />
                  : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                      {name.charAt(0)}
                    </div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="text-sm font-medium th-text truncate">{name}</p>
                  <p className="text-xs th-muted">
                    {format(parseISO(r.from), 'MMM d')}
                    {r.from !== r.to && ` – ${format(parseISO(r.to), 'MMM d')}`}
                    {duration && ` · ${duration}`}
                  </p>
                </div>
                <LeaveTypeBadge type={r.type} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}